import {
  WorkspaceRateLimit,
  useGetWalletDetailsV2Query,
  useGetLowBalanceAlertQuery,
  useListSubscriptionQuery,
  useProviderLoginQuery,
  useWorkspaceRateLimitsQuery,
  useworkspaceAuthMeV2Query,
} from '@/types/generated/graphql';
import { makeVar, useReactiveVar } from '@apollo/client';
import { ROLE_MAPS } from '@shared/constants/roles';
import { hasRoleAccess } from '@shared/utils/roles/has-role-access.helper';
import { getMaxUsedPercent, getParsedLimits } from '@components/rate-limit/helper';
import { useResumption } from '@modules/settings/subscription/hooks/use-resumption';
import { formatDate } from '@modules/settings/subscription/utils/utils';
import { cacheOnly, WALLET_AMOUNT_THRESHOLDS } from '@shared/constants';
import { useModal } from '@shared/hooks/use-modal';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMfaReminderBanner } from './use-mfa-reminder-banner';
import { useShowSwitchCall } from './use-show-switch-call';
import { useUserRole } from '@shared/hooks/use-user-role';

const _twilioTokenError = ['There was a problem with your account configuration. Please contact customer support.'];

const PLAN_RANK: Record<string, number> = {
  'basic-month': 0,
  'premium-month': 1,
  'basic-year': 2,
  'premium-year': 3,
};

export const useAppBanner = () => {
  const navigate = useNavigate();
  const { rateLimits } = useWorkspaceRateLimit();
  const mfaBanner = useMfaReminderBanner();
  const { shouldShowSwitchCall } = useShowSwitchCall();

  //wallet logic
  const { data: walletDetailData } = useGetWalletDetailsV2Query({ fetchPolicy: 'cache-and-network' });
  const walletAmount = (walletDetailData?.getWalletDetailsV2?.data?.amount ?? 0) / 100;
  const { data: lowBalanceAlertData } = useGetLowBalanceAlertQuery();
  const lowBalanceThreshold = (lowBalanceAlertData?.getLowBalanceAlert?.data?.balanceThreshold ?? 0) / 100;
  const isWalletBalanceLow = !!walletAmount && walletAmount < lowBalanceThreshold;
  const isWalletBalanceMediumLow = !!walletAmount && walletAmount < WALLET_AMOUNT_THRESHOLDS.MEDIUM;
  const handleNavigateToWallet = () => navigate('/settings/wallet?tab=Overview&openModal=loadWallet');
  const userRole = useUserRole();
  //subscription logic
  const resumeController = useResumption();
  const { data, loading: loadingSubscription } = useListSubscriptionQuery({
    fetchPolicy: 'cache-first',
    skip: userRole?.isMember,
  });
  const workspaceSubscription = data?.listSubscription?.edges?.find(
    (sub) => sub?.itemProductFamily === 'calilio-workspace'
  );
  const formattedDate = workspaceSubscription?.currentTermEnd && formatDate(workspaceSubscription?.currentTermEnd);
  const isNonRenewing = workspaceSubscription?.status === 'non_renewing';
  const currentPlan = workspaceSubscription?.subscriptionItems?.[0]?.itemPriceId?.split('-')?.[2];
  const currentBillingPeriod = workspaceSubscription?.billingPeriodUnit?.startsWith('year') ? 'year' : 'month';
  const currentPlanRank = PLAN_RANK[`${currentPlan}-${currentBillingPeriod}`];

  const nextSubscriptionPlan = workspaceSubscription?.hasScheduledChanges
    ? workspaceSubscription?.scheduledChanges?.subscriptionItems?.[0]?.itemPriceId?.split('-')?.[2]
    : '';
  const nextBillingPeriod = workspaceSubscription?.scheduledChanges?.billingPeriodUnit?.startsWith('year')
    ? 'year'
    : 'month';
  const nextPlanRank = PLAN_RANK[`${nextSubscriptionPlan}-${nextBillingPeriod}`];

  const isScheduledDowngradeToBasic = !!workspaceSubscription?.hasScheduledChanges && nextPlanRank < currentPlanRank;

  //twilio token logic
  const { error: twilioTokenError, loading: loadingProviderLogin } = useProviderLoginQuery();
  const hasTwilioTokenError = _twilioTokenError.includes(twilioTokenError?.message || '');

  const [hasPreferredContactRoleAccess] = hasRoleAccess(ROLE_MAPS.SETTINGS_MYWORKSPACE_SETPREFEREDCONTACT);

  //AccountSuspensionWarning logic
  const { data: workspaceData, loading: loadingWksAuthme } = useworkspaceAuthMeV2Query();
  const hasAccountSuspensionWarning = workspaceData?.workspaceAuthMeV2?.data?.subscription?.status === 'payment_due';
  const transactionDate = workspaceData?.workspaceAuthMeV2?.data?.subscription?.transactionDate;
  const transactionFailedDays = transactionDate ? dayjs().diff(dayjs(transactionDate), 'day') : 0;
  const remainingDays = 3 - transactionFailedDays;
  const paymentModal = useModal();

  //AccountSuspendedByInsuffientBalance logic
  const isSuspendedByInsufficientBalance =
    workspaceData?.workspaceAuthMeV2?.data?.providerAccount?.status === 'suspended' &&
    workspaceData?.workspaceAuthMeV2?.data?.providerAccount?.systemSuspend === 'low_wallet_balance';

  //AccountSuspended Banner logic
  const showAccountSuspendedBanner = workspaceData?.workspaceAuthMeV2?.data?.providerAccount?.status === 'suspended';

  //call not supported logic
  const webRtcPeerConnectionEnabled = makeVar<boolean>(true);
  const webRtcPeerConnection = useReactiveVar(webRtcPeerConnectionEnabled);
  const showCallNotSupportedBanner = !webRtcPeerConnection;

  //micPermission logic
  const [micPermission, setMicPermission] = useState<PermissionState | undefined>();
  const hasMicPermission = !(micPermission === 'denied');
  const permissionName = 'microphone' as PermissionName;
  useEffect(() => {
    // some browsers (e.g. Safari/Firefox) reject the 'microphone' permission name — leave the state unknown
    navigator?.permissions
      ?.query({ name: permissionName })
      .then((permissionStatus: PermissionStatus) => {
        setMicPermission(permissionStatus?.state);
        permissionStatus.onchange = () => {
          setMicPermission(permissionStatus?.state);
        };
      })
      .catch(() => setMicPermission(undefined));
  }, []);

  // notificationPermission logic
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>();
  const isNotificationSilent = notificationPermission === 'denied';
  useEffect(() => {
    if (!('permissions' in navigator)) return;
    navigator.permissions
      .query({ name: 'notifications' as PermissionName })
      .then((permissionStatus: PermissionStatus) => {
        setNotificationPermission(permissionStatus.state as NotificationPermission);
        permissionStatus.onchange = () => {
          setNotificationPermission(permissionStatus.state as NotificationPermission);
        };
      });
  }, []);

  const preferredContactReminderAt = workspaceData?.workspaceAuthMeV2?.data?.preferredContactMethodReminderAt;
  const hasPreferredContact =
    hasPreferredContactRoleAccess &&
    !!preferredContactReminderAt &&
    dayjs(preferredContactReminderAt).valueOf() <= dayjs().valueOf();

  //loading state
  const bannerLoading = loadingProviderLogin || loadingWksAuthme || loadingSubscription;

  return {
    hasPreferredContact,
    rateLimits,
    isWalletBalanceLow,
    isWalletBalanceMediumLow,
    handleNavigateToWallet,
    resumeController,
    isNonRenewing,
    formattedDate,
    isScheduledDowngradeToBasic,
    hasTwilioTokenError,
    hasAccountSuspensionWarning,
    remainingDays,
    paymentModal,
    isSuspendedByInsufficientBalance,
    showAccountSuspendedBanner,
    showCallNotSupportedBanner,
    hasMicPermission,
    bannerLoading,
    workspaceSubscription,
    isNotificationSilent,
    showMfaReminder: mfaBanner.showMfaReminder(),
    mfaBanner,
    showSwitchCall: shouldShowSwitchCall,
  };
};

function useWorkspaceRateLimit() {
  const { data } = useWorkspaceRateLimitsQuery(cacheOnly);
  const parsedLimits = getParsedLimits(data?.workspaceRateLimits?.data as WorkspaceRateLimit[]);
  const maxUsedPercent = getMaxUsedPercent(parsedLimits);

  return {
    rateLimits: maxUsedPercent >= 100,
  };
}
